import { useEffect, useMemo, useState } from 'react';
import {
    fetchCopyPatternDetail,
    fetchCopyPatternList,
    fetchCopyPatternSummary,
    type CopyPatternDetailResult,
    type CopyPatternRow,
    type CopyPatternStatus,
    type FieldType,
    type PatternType
} from '../lib/copyPatternEfficiency';

const PAGE_SIZE = 50;

function formatPercent(value: number): string {
    return `${(value * 100).toFixed(2)}%`;
}

function formatDateLabel(value: string | null | undefined): string {
    if (!value) return 'n/a';
    return new Date(value).toLocaleDateString('zh-TW');
}

function statusLabel(status: CopyPatternStatus): string {
    switch (status) {
        case 'INVALID': return '無效';
        case 'INEFFICIENT': return '低效';
        default: return status;
    }
}

function statusColor(status: CopyPatternStatus): string {
    switch (status) {
        case 'INVALID': return '#B42318';
        case 'INEFFICIENT': return '#DC6803';
        default: return '#667085';
    }
}

export function CopyPatternEfficiencyDashboard() {
    const [fieldType] = useState<FieldType>('HEADLINE');
    const [status, setStatus] = useState<CopyPatternStatus | 'ALL'>('ALL');
    const [patternType, setPatternType] = useState<PatternType | 'ALL'>('ALL');
    const [search, setSearch] = useState('');
    const [dateFrom, setDateFrom] = useState('2023-01-01');
    const [dateTo, setDateTo] = useState('');
    const [sort, setSort] = useState('replacePriority');

    const [patterns, setPatterns] = useState<CopyPatternRow[]>([]);
    const [total, setTotal] = useState(0);
    const [selectedPatternKey, setSelectedPatternKey] = useState<string>('');
    const [detail, setDetail] = useState<CopyPatternDetailResult | null>(null);
    const [summary, setSummary] = useState<{
        totalPatterns: number;
        invalid: number;
        inefficient: number;
        latestDataDate: string | null;
    } | null>(null);

    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [error, setError] = useState('');

    const hasMore = patterns.length < total;

    const queryParams = useMemo(() => ({
        fieldType,
        status,
        patternType,
        search: search.trim() || undefined,
        dateFrom,
        dateTo: dateTo || undefined
    }), [fieldType, status, patternType, search, dateFrom, dateTo]);

    useEffect(() => {
        void loadInitial();
    }, [queryParams, sort]);

    useEffect(() => {
        if (!selectedPatternKey) {
            setDetail(null);
            return;
        }
        void loadDetail(selectedPatternKey);
    }, [selectedPatternKey, fieldType, dateFrom, dateTo]);

    async function loadInitial() {
        try {
            setLoading(true);
            setError('');
            setSelectedPatternKey('');

            const [summaryRes, listRes] = await Promise.all([
                fetchCopyPatternSummary(queryParams),
                fetchCopyPatternList({
                    ...queryParams,
                    sort,
                    limit: PAGE_SIZE,
                    offset: 0
                })
            ]);

            if (!summaryRes.success || !summaryRes.data) {
                throw new Error(summaryRes.error || '載入 summary 失敗');
            }
            if (!listRes.success || !listRes.data) {
                throw new Error(listRes.error || '載入 patterns 失敗');
            }

            setSummary({
                ...summaryRes.data.summary,
                latestDataDate: summaryRes.data.dateRange.latestDataDate
            });
            setPatterns(listRes.data.items);
            setTotal(listRes.data.total);

            if (!dateTo && summaryRes.data.dateRange.latestDataDate) {
                setDateTo(summaryRes.data.dateRange.latestDataDate);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : '載入低效文案 dashboard 失敗');
        } finally {
            setLoading(false);
        }
    }

    async function loadMore() {
        try {
            setLoadingMore(true);
            const res = await fetchCopyPatternList({
                ...queryParams,
                sort,
                limit: PAGE_SIZE,
                offset: patterns.length
            });
            if (!res.success || !res.data) {
                throw new Error(res.error || '載入更多 patterns 失敗');
            }
            setPatterns((current) => [...current, ...res.data!.items]);
            setTotal(res.data.total);
        } catch (err) {
            setError(err instanceof Error ? err.message : '載入更多資料失敗');
        } finally {
            setLoadingMore(false);
        }
    }

    async function loadDetail(patternKey: string) {
        try {
            setLoadingDetail(true);
            const res = await fetchCopyPatternDetail(patternKey, {
                fieldType,
                dateFrom,
                dateTo: dateTo || undefined
            });
            if (!res.success || !res.data) {
                throw new Error(res.error || '載入 pattern 明細失敗');
            }
            setDetail(res.data);
        } catch (err) {
            setError(err instanceof Error ? err.message : '載入 pattern 明細失敗');
            setDetail(null);
        } finally {
            setLoadingDetail(false);
        }
    }

    return (
        <div>
            <div style={{ marginBottom: '2rem' }}>
                <h2 style={{ fontSize: '1.9rem', marginBottom: '0.5rem' }}>🧪 低效文案模式分析</h2>
                <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                    這頁目前只分析 RSA 的單一句標題，且只納入符合 RSA headline 長度限制的句子，不分析描述。結構欄位只拿來幫你看句型，不再先把不同句子合成同一筆。資料範圍：
                    {summary?.latestDataDate ? ` ${formatDateLabel(dateFrom)} 到 ${formatDateLabel(summary.latestDataDate)}` : ' 全歷史'}。
                </p>
            </div>

            <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '0.75rem' }}>
                    <div>
                        <label className="form-label">資料起始日</label>
                        <input className="form-input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    </div>
                    <div>
                        <label className="form-label">資料截止日</label>
                        <input className="form-input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                    </div>
                    <div>
                        <label className="form-label">分析範圍</label>
                        <div className="form-input" style={{ display: 'flex', alignItems: 'center', color: 'var(--color-text-secondary)' }}>
                            Headlines only
                        </div>
                    </div>
                    <div>
                        <label className="form-label">狀態</label>
                        <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value as CopyPatternStatus | 'ALL')}>
                            <option value="ALL">全部</option>
                            <option value="INVALID">無效</option>
                            <option value="INEFFICIENT">低效</option>
                        </select>
                    </div>
                    <div>
                        <label className="form-label">模式類型</label>
                        <select className="form-select" value={patternType} onChange={(e) => setPatternType(e.target.value as PatternType | 'ALL')}>
                            <option value="ALL">全部</option>
                            <option value="Persona特式化">Persona特式化</option>
                            <option value="活動特式化">活動特式化</option>
                            <option value="見證代言特式化">見證代言特式化</option>
                        </select>
                    </div>
                    <div>
                        <label className="form-label">搜尋 / 排序</label>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input
                                className="form-input"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="搜尋代表句或 pattern"
                            />
                            <select className="form-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                                <option value="replacePriority">優先淘汰</option>
                                <option value="ctr">CTR 低到高</option>
                                <option value="cvr">CVR 低到高</option>
                                <option value="conversions">轉換少到多</option>
                                <option value="impressions">曝光高到低</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {error && (
                <div className="card" style={{ marginBottom: '1rem', borderColor: 'var(--color-danger)', color: 'var(--color-danger)' }}>
                    {error}
                </div>
            )}

            {summary && (
                <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
                    <div className="stat-card">
                        <div className="stat-label">無效</div>
                        <div className="stat-value danger">{summary.invalid}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">低效</div>
                        <div className="stat-value danger">{summary.inefficient}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">低效標題總數</div>
                        <div className="stat-value">{summary.invalid + summary.inefficient}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">目前列出標題數</div>
                        <div className="stat-value">{summary.totalPatterns}</div>
                    </div>
                </div>
            )}

            <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h3 className="card-title">低效標題清單</h3>
                {loading ? (
                    <div className="loading"><div className="spinner"></div></div>
                ) : (
                    <>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                                        <th style={{ textAlign: 'left', padding: '0.75rem' }}>結構</th>
                                        <th style={{ textAlign: 'left', padding: '0.75rem' }}>標題</th>
                                        <th style={{ textAlign: 'right', padding: '0.75rem' }}>曝光</th>
                                        <th style={{ textAlign: 'right', padding: '0.75rem' }}>點擊</th>
                                        <th style={{ textAlign: 'right', padding: '0.75rem' }}>轉換</th>
                                        <th style={{ textAlign: 'right', padding: '0.75rem' }}>CTR</th>
                                        <th style={{ textAlign: 'right', padding: '0.75rem' }}>CVR</th>
                                        <th style={{ textAlign: 'left', padding: '0.75rem' }}>狀態</th>
                                        <th style={{ textAlign: 'right', padding: '0.75rem' }}>CTR十分位</th>
                                        <th style={{ textAlign: 'right', padding: '0.75rem' }}>CVR十分位</th>
                                        <th style={{ textAlign: 'left', padding: '0.75rem' }}>說明</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {patterns.map((pattern) => (
                                        <tr
                                            key={pattern.patternKey}
                                            onClick={() => setSelectedPatternKey(pattern.patternKey)}
                                            style={{
                                                borderTop: '1px solid var(--color-border)',
                                                cursor: 'pointer',
                                                background: selectedPatternKey === pattern.patternKey ? 'var(--color-primary-light)' : 'transparent'
                                            }}
                                        >
                                            <td style={{ padding: '0.75rem', whiteSpace: 'nowrap' }}>
                                                <div style={{ fontWeight: 600 }}>{pattern.patternLabel}</div>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                                                    {pattern.fieldType}
                                                </div>
                                            </td>
                                            <td style={{ padding: '0.75rem', minWidth: '280px' }}>{pattern.representativeText}</td>
                                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>{pattern.impressions.toLocaleString()}</td>
                                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>{pattern.clicks.toLocaleString()}</td>
                                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>{pattern.conversions.toLocaleString()}</td>
                                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatPercent(pattern.ctr)}</td>
                                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatPercent(pattern.cvr)}</td>
                                            <td style={{ padding: '0.75rem', color: statusColor(pattern.status), fontWeight: 700 }}>
                                                {statusLabel(pattern.status)}
                                            </td>
                                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>{pattern.ctrDecile}/10</td>
                                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>{pattern.cvrDecile}/10</td>
                                            <td style={{ padding: '0.75rem', minWidth: '300px', color: 'var(--color-text-secondary)' }}>
                                                {pattern.customerNarrative}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {hasMore && (
                            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
                                <button className="btn btn-secondary" onClick={loadMore} disabled={loadingMore}>
                                    {loadingMore ? '載入中…' : '載入更多'}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="card">
                <h3 className="card-title">同結構標題 Drill-down</h3>
                {!selectedPatternKey ? (
                    <p style={{ color: 'var(--color-text-secondary)' }}>點選上方任一標題，可下鑽查看同結構的其他句子與常見分布。</p>
                ) : loadingDetail ? (
                    <div className="loading"><div className="spinner"></div></div>
                ) : detail?.pattern ? (
                    <div style={{ display: 'grid', gap: '1rem' }}>
                        <div style={{ padding: '1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                                <div>
                                    <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>{detail.pattern.patternLabel}</div>
                                    <div style={{ color: 'var(--color-text-secondary)', marginTop: '0.35rem' }}>{detail.pattern.representativeText}</div>
                                </div>
                                <div style={{ color: statusColor(detail.pattern.status), fontWeight: 700 }}>
                                    {statusLabel(detail.pattern.status)}
                                </div>
                            </div>
                            <div style={{ marginTop: '0.75rem', color: 'var(--color-text-secondary)' }}>{detail.pattern.whyItIsBad}</div>
                            <div style={{ marginTop: '0.5rem', color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
                                CTR 第 {detail.pattern.ctrDecile} 十分位 / CVR 第 {detail.pattern.cvrDecile} 十分位
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '1rem' }}>
                            <div style={{ padding: '1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }}>
                                <div style={{ fontWeight: 700, marginBottom: '0.75rem' }}>常見 Specialization</div>
                                {detail.specializationDistribution.slice(0, 5).map((item) => (
                                    <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                                        <span>{item.label}</span>
                                        <span>{item.impressions.toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                            <div style={{ padding: '1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }}>
                                <div style={{ fontWeight: 700, marginBottom: '0.75rem' }}>常見 Persona</div>
                                {detail.personaDistribution.slice(0, 5).map((item) => (
                                    <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                                        <span>{item.label}</span>
                                        <span>{item.impressions.toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                            <div style={{ padding: '1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }}>
                                <div style={{ fontWeight: 700, marginBottom: '0.75rem' }}>常見 Query Family</div>
                                {detail.queryFamilyDistribution.slice(0, 5).map((item) => (
                                    <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                                        <span>{item.label}</span>
                                        <span>{item.impressions.toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1rem' }}>
                            <div style={{ padding: '1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }}>
                                <div style={{ fontWeight: 700, marginBottom: '0.75rem' }}>最佳句</div>
                                {detail.bestInstances.map((item, index) => (
                                    <div key={`${item.assetText}-${index}`} style={{ marginBottom: '0.75rem' }}>
                                        <div style={{ fontWeight: 600 }}>{item.assetText}</div>
                                        <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                                            {item.campaignName} / {item.adGroupName} · CTR {formatPercent(item.ctr)} · CVR {formatPercent(item.cvr)} · Conv {item.conversions}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ padding: '1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }}>
                                <div style={{ fontWeight: 700, marginBottom: '0.75rem' }}>最差句</div>
                                {detail.worstInstances.map((item, index) => (
                                    <div key={`${item.assetText}-${index}`} style={{ marginBottom: '0.75rem' }}>
                                        <div style={{ fontWeight: 600 }}>{item.assetText}</div>
                                        <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                                            {item.campaignName} / {item.adGroupName} · CTR {formatPercent(item.ctr)} · CVR {formatPercent(item.cvr)} · Conv {item.conversions}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                                        <th style={{ textAlign: 'left', padding: '0.75rem' }}>句子</th>
                                        <th style={{ textAlign: 'left', padding: '0.75rem' }}>Campaign / Ad Group</th>
                                        <th style={{ textAlign: 'left', padding: '0.75rem' }}>Persona / Query</th>
                                        <th style={{ textAlign: 'right', padding: '0.75rem' }}>曝光</th>
                                        <th style={{ textAlign: 'right', padding: '0.75rem' }}>CTR</th>
                                        <th style={{ textAlign: 'right', padding: '0.75rem' }}>CVR</th>
                                        <th style={{ textAlign: 'right', padding: '0.75rem' }}>Conv</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detail.instances.map((item, index) => (
                                        <tr key={`${item.assetText}-${item.adId}-${index}`} style={{ borderTop: '1px solid var(--color-border)' }}>
                                            <td style={{ padding: '0.75rem', minWidth: '260px' }}>{item.assetText}</td>
                                            <td style={{ padding: '0.75rem' }}>
                                                <div>{item.campaignName}</div>
                                                <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>{item.adGroupName}</div>
                                            </td>
                                            <td style={{ padding: '0.75rem' }}>
                                                <div>{item.persona}</div>
                                                <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>{item.specialization} / {item.queryFamily}</div>
                                            </td>
                                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>{item.impressions.toLocaleString()}</td>
                                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatPercent(item.ctr)}</td>
                                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatPercent(item.cvr)}</td>
                                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>{item.conversions.toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <p style={{ color: 'var(--color-text-secondary)' }}>找不到該 pattern 的明細資料。</p>
                )}
            </div>
        </div>
    );
}
